// IMPORTANT!!!
// Types were declared in the same file due to the file-sync extension.
// As we save here in @libs/authorization, it will automatically sync the code into
// @apps/supabase/supabase/functions/_shared/utils/authorization.ts.

import {
  AbilityBuilder,
  subject as caslSubject,
  createMongoAbility,
  MongoAbility,
} from "@casl/ability";

import { Database } from "@libs/schemas";

export type UserRoleType =
  | Database["public"]["Enums"]["staff_role_enum"]
  | "STUDENT";

type StaffType = { id: string };
type StudentsType = { id: string };
type EnrolmentType = { id: string };

export type ActionsType =
  | "create"
  | "read"
  | "list"
  | "update"
  | "delete"
  | "decrypt";
export type DBTablesType = keyof Database["public"]["Tables"];
type SubjectsType = StaffType | StudentsType | EnrolmentType;

type AppAbilityType = MongoAbility<[ActionsType, SubjectsType | DBTablesType]>;
type RulesType = AppAbilityType["rules"];

const allActions: ActionsType[] = [
  "create",
  "read",
  "list",
  "update",
  "delete",
  "decrypt",
];

export type AbilityRequestType = {
  userId: string;
  userRole: UserRoleType | null;
  campusId: string | null;
  enrolmentId?: string | null;
};

// ---------------------- //

export const getUserRules = ({
  userId,
  userRole,
  campusId,
  enrolmentId,
}: AbilityRequestType): RulesType => {
  const { can, cannot, rules } = new AbilityBuilder<AppAbilityType>(
    createMongoAbility,
  );

  switch (userRole) {
    // Owner has access to all actions and tables
    case "OWNER":
      can(allActions, "staff");
      can(allActions, "students");
      can(allActions, "courses");
      can(allActions, "locations");
      can(allActions, "campuses");

      can(allActions, "enrolments");
      can(allActions, "enrolments_notes");
      can(allActions, "enrolments_products");

      can(allActions, "events");

      can(allActions, "evaluations");

      can(allActions, "enrolments_achievements");
      can(allActions, "events_activities");
      can(allActions, "events_vehicles");

      can(allActions, "enrolments_classrooms");

      can(allActions, "vehicles");

      can(allActions, "contracts");

      can(allActions, "enrolments_documents_types");

      can(allActions, "document_types");

      can(allActions, "enrolment_general_documents");

      can(allActions, "enrolments_events");

      can(allActions, "payments");
      can(allActions, "invoices");
      can(allActions, "invoice_items");
      can(allActions, "invoice_payment_schedule");
      can(allActions, "payers");

      can(allActions, "products");

      break;

    case "CENTRAL_MANAGER":
      can("update", "staff", { id: userId });
      can("read", "staff");
      can("list", "staff");

      can("create", "students");
      can("read", "students");
      can("list", "students");
      can("update", "students");
      can("decrypt", "students");

      can("create", "courses");
      can("read", "courses");
      can("list", "courses");
      can("update", "courses");

      can("create", "locations");
      can("update", "locations");
      can("read", "locations");
      can("list", "locations");

      can("create", "campuses");
      can("update", "campuses");
      can("read", "campuses");
      can("list", "campuses");

      can("create", "enrolments");
      can("read", "enrolments");
      can("update", "enrolments");
      can("delete", "enrolments");
      can("list", "enrolments");

      can("create", "enrolments_notes");
      can("read", "enrolments_notes");
      can("update", "enrolments_notes");
      can("list", "enrolments_notes");

      can("create", "enrolments_products");
      can("read", "enrolments_products");
      can("update", "enrolments_products");
      can("delete", "enrolments_products");
      can("list", "enrolments_products");

      can("create", "events");
      can("update", "events");
      can("read", "events");
      can("list", "events");

      can("create", "enrolments_achievements", { id: enrolmentId });
      can("update", "enrolments_achievements", { id: enrolmentId });

      can("create", "enrolments_classrooms", { id: enrolmentId });
      can("update", "enrolments_classrooms", { id: enrolmentId });
      can("list", "enrolments_classrooms");
      can("read", "enrolments_classrooms");

      can("create", "events_activities");
      can("update", "events_activities");
      can("read", "events_activities");

      can("create", "events_vehicles");
      can("update", "events_vehicles");
      can("read", "events_vehicles");

      can("create", "vehicles");
      can("update", "vehicles");
      can("read", "vehicles");
      can("list", "vehicles");

      can("create", "evaluations");
      can("update", "evaluations");
      can("read", "evaluations");
      can("list", "evaluations");

      can("create", "contracts");
      can("update", "contracts");

      can("create", "enrolments_documents_types");
      can("update", "enrolments_documents_types");
      can("read", "enrolments_documents_types");
      can("delete", "enrolments_documents_types");

      can("create", "document_types");
      can("read", "document_types");
      can("list", "document_types");
      can("update", "document_types");

      can("create", "enrolment_general_documents");
      can("update", "enrolment_general_documents");
      can("read", "enrolment_general_documents");
      can("delete", "enrolment_general_documents");

      can("update", "enrolments_events");

      can("create", "payments");
      can("read", "payments");
      can("list", "payments");
      can("update", "payments");

      can("create", "invoices");
      can("read", "invoices");
      can("list", "invoices");
      can("update", "invoices");
      can("delete", "invoices");

      can("create", "invoice_items");
      can("read", "invoice_items");
      can("list", "invoice_items");
      can("update", "invoice_items");
      can("delete", "invoice_items");

      can("create", "invoice_payment_schedule");
      can("read", "invoice_payment_schedule");
      can("list", "invoice_payment_schedule");
      can("update", "invoice_payment_schedule");
      can("delete", "invoice_payment_schedule");

      can("create", "payers");
      can("read", "payers");
      can("list", "payers");
      can("update", "payers");
      can("delete", "payers");

      can("create", "products");
      can("read", "products");
      can("list", "products");
      can("update", "products");

      break;

    case "CSR":
      can("update", "staff", { id: userId });

      can("create", "students");
      can("read", "students");
      can("list", "students");
      can("update", "students");

      can("read", "courses");
      can("read", "campuses");

      can("create", "enrolments");
      can("read", "enrolments");
      can("list", "enrolments");

      can("create", "enrolments_notes");
      can("read", "enrolments_notes");
      can("update", "enrolments_notes");
      can("list", "enrolments_notes");

      can("create", "enrolments_products");
      can("read", "enrolments_products");
      can("list", "enrolments_products");

      can("read", "enrolments_achievements");
      can("create", "enrolments_achievements", { id: enrolmentId });

      can("create", "enrolments_classrooms", { id: enrolmentId });

      can("create", "enrolments_documents_types");
      can("update", "enrolments_documents_types");
      can("read", "enrolments_documents_types");

      can("read", "document_types");
      can("list", "document_types");

      can("create", "enrolment_general_documents");
      can("update", "enrolment_general_documents");
      can("read", "enrolment_general_documents");

      can("create", "evaluations");
      can("update", "evaluations");
      can("read", "evaluations");
      can("list", "evaluations");

      can("read", "vehicles");
      can("list", "vehicles");

      can("update", "contracts");

      can("read", "products");
      can("list", "products");

      can("read", "invoices");
      can("list", "invoices");

      can("read", "invoice_items");
      can("list", "invoice_items");

      can("read", "invoice_payment_schedule");
      can("list", "invoice_payment_schedule");

      can("read", "payers");
      can("list", "payers");

      break;

    case "CAMPUS_MANAGER":
      can("update", "staff", { id: userId });

      can("create", "students");
      can("read", "students");
      can("update", "students");
      can("list", "students");
      can("decrypt", "students");

      can("read", "courses");
      can("read", "campuses", {
        id: campusId,
      });

      can("read", "locations");
      can("list", "locations");

      can("create", "enrolments");
      can("read", "enrolments");
      can("list", "enrolments");
      can("update", "enrolments");

      can("create", "enrolments_notes");
      can("read", "enrolments_notes");
      can("update", "enrolments_notes");
      can("list", "enrolments_notes");

      can("read", "enrolments_products");
      can("list", "enrolments_products");

      can("create", "events");
      can("update", "events");
      cannot("update", "events", ["instructor_signature"]);

      can("read", "enrolments_events");
      can("create", "enrolments_events");
      can("update", "enrolments_events");
      cannot("update", "enrolments_events", ["student_signature"]);

      can("read", "events");
      can("list", "events");

      can("read", "enrolments_achievements");

      can("create", "enrolments_classrooms", { id: enrolmentId });

      can("create", "events_activities");
      can("update", "events_activities");
      can("read", "events_activities");

      can("create", "events_vehicles");
      can("update", "events_vehicles");
      can("read", "events_vehicles");

      can("update", "vehicles");
      can("read", "vehicles");
      can("list", "vehicles");

      can("create", "evaluations");
      can("update", "evaluations");
      can("read", "evaluations");
      can("list", "evaluations");

      can("create", "enrolments_documents_types");
      can("update", "enrolments_documents_types");
      can("read", "enrolments_documents_types");

      can("read", "document_types");
      can("list", "document_types");

      can("create", "enrolment_general_documents");
      can("update", "enrolment_general_documents");
      can("read", "enrolment_general_documents");

      can("update", "contracts");

      can("create", "payments");
      can("read", "payments");
      can("list", "payments");

      can("read", "invoices");
      can("list", "invoices");

      can("read", "invoice_items");
      can("list", "invoice_items");

      can("read", "invoice_payment_schedule");
      can("list", "invoice_payment_schedule");

      can("read", "payers");
      can("list", "payers");

      can("read", "products");
      can("list", "products");

      can("decrypt", "students");

      break;

    case "INSTRUCTOR":
      can("update", "staff", { id: userId });

      can("read", "students");
      can("list", "students");

      can("read", "courses");

      can("update", "events");
      cannot("update", "events", [
        "is_published",
        "staff_notes",
        "student_notes",
      ]);

      can("read", "events");
      can("list", "events");

      can("create", "evaluations");
      can("read", "evaluations");
      can("list", "evaluations");

      can("read", "document_types");
      can("list", "document_types");

      can("read", "enrolments_achievements");

      can("read", "events_activities");

      can("create", "events_vehicles");
      can("update", "events_vehicles");
      can("read", "events_vehicles");

      can("read", "vehicles");
      can("list", "vehicles");

      can("update", "enrolments_events");

      can("create", "enrolments_notes");
      can("read", "enrolments_notes");
      can("update", "enrolments_notes");
      can("list", "enrolments_notes");

      can("read", "products");
      can("list", "products");

      break;

    case "STUDENT":
      can("create", "students", { id: userId });
      can("read", "students", { id: userId });
      can("update", "students", { id: userId });
      can("delete", "students", { id: userId });

      can("read", "courses");

      can("list", "events");
      can("read", "events");

      can("read", "campuses", {
        id: campusId,
      });

      can("read", "evaluations");

      can("read", "enrolments_achievements");

      can("read", "events_activities");

      can("read", "events_vehicles");

      can("update", "enrolments_events", ["student_signature"]);

      can("read", "enrolments_notes");
      can("list", "enrolments_notes");

      break;

    default:
      allActions.forEach(action => {
        cannot(action, "staff");
        cannot(action, "students");
        cannot(action, "courses");
        cannot(action, "enrolments");
        cannot(action, "enrolments_products");
        cannot(action, "enrolments_achievements");
        cannot(action, "evaluations");
      });
      break;
  }

  return rules;
};

export const abilities = ({
  userId,
  userRole,
  campusId,
}: AbilityRequestType) => {
  return createMongoAbility<[ActionsType, SubjectsType | DBTablesType]>(
    getUserRules({ userId, userRole, campusId }),
  );
};

export const parseSubjectResource = ({
  subject,
  subjectResource,
}: {
  subject: DBTablesType;
  subjectResource?: object;
}) => {
  return (subjectResource ? caslSubject(subject, subjectResource) : subject) as
    | SubjectsType
    | DBTablesType;
};
